<script setup lang="ts">
import { ref, watch } from 'vue';
import { add } from 'date-fns';
import type { DatePickerInstance } from '@vuepic/vue-datepicker';

interface Props {
  open: boolean
  minRange: number
  maxRange: number | null
  disabledDates: Date[]
  maxQuantity: number
  selectedDate?: [Date, Date]
}

const props = defineProps<Props>();

const selectedDate = ref(props.selectedDate);

const emit = defineEmits<{
  (e: 'updateRange', value: Date[]): void,
  (e: 'updateQuantity', value: number): void,
  (e: 'close'): void
}>();

const allowSameDay = import.meta.env.VITE_ALLOW_SAME_DAY === 'true';
// eslint-disable-next-line no-magic-numbers
const minimumDate = add(new Date(), { days: allowSameDay ? 0 : 2 });

function handleRange(val:Date[]) {
  if (val[1]) {
    emit('updateRange', val);
  } else {
    emit('updateRange', [val[0], val[0]]);
  }
}

const rangeOptions = {
  minRange: props.minRange - 1,
  maxRange: props.maxRange || undefined,
  noDisabledRange: true,

};

const startTime = [{ hours: 12, minutes: 0, seconds: 0 }, { hours: 12, minutes: 0, seconds: 0 }];

const datePicker = ref<DatePickerInstance>(null);

function selectDate() {
  if (datePicker.value) datePicker.value.selectDate();
}

const quantity = ref(1);

watch(quantity, () => {
  emit('updateQuantity', quantity.value);
});
</script>
<template>
  <base-modal
    :open="open"
    @close="$emit('close')"
  >
    <div class="relative flex w-full flex-col">
      <template
        v-if="maxQuantity > 1"
      >
        <p
          class="text-lg font-bold text-z-gray-800"
        >
          ¿Cuántos productos deseas arrendar?
        </p>
        <base-counter
          v-model="quantity"
          label="Cantidad de productos a arrendar"
          hide-label
          :min="1"
          unit="productos"
          name="quantity"
          class="mb-4"
        />
      </template>
      <p
        class="text-lg font-bold text-z-gray-800"
      >
        ¿Cuántos días deseas arrendar?
      </p>
      <div class="mt-2 flex w-full justify-between text-sm text-z-gray-600">
        <p>Mínimo: <span class="font-medium text-z-gray-700">{{ minRange }}</span></p>
        <p>Máximo: <span class="font-medium text-z-gray-700">{{ maxRange === 0 || !maxRange ? 'sin límite' : maxRange }}</span></p>
      </div>
      <div class="relative flex h-[348px] w-full flex-col items-center">
        <VueDatePicker
          ref="datePicker"
          v-model="selectedDate"
          inline
          :range="rangeOptions"
          :no-today="!allowSameDay"
          locale="es"
          :enable-time-picker="false"
          :min-date="minimumDate"
          :disabled-dates="disabledDates"
          :day-names="['L', 'M', 'M', 'J', 'V', 'S', 'D']"
          :start-time="startTime"
          prevent-min-max-navigation
          class="mt-4"
          vertical
          @update:model-value="(val:Date[]) => handleRange(val)"
        >
          <template #action-row />
        </VueDatePicker>
      </div>
      <div class="flex w-full">
        <base-button
          class="w-full"
          @click="selectDate"
        >
          Continuar
        </base-button>
      </div>
    </div>
  </base-modal>
</template>
<style>
.dp__menu {
  border: 0 !important;
}
.dp__theme_light {
  --dp-primary-color: #00a48f;
  --dp-range-between-dates-background-color: var(--dp-primary-color);
  --dp-range-between-dates-text-color: var(--dp-primary-text-color);
  --dp-text-color: #202020;
  --dp-disabled-color-text: #9f9f9f;
  --dp-border-color: #f2f2f2;
  --dp-future-offset-color: #6F6F6F;
}
:root {
  --dp-cell-border-radius: 100px;
  --dp-font-family: 'Poppins', sans-serif;
  --dp-row-margin: 8px 0;
  --dp-cell-size: 100%;
  --dp-menu-padding: 0;
  --dp-action-row-padding: 0;
}
.dp__calendar_row {
  height: 32px;
}
.dp__calendar_item {
  height: 32px;
  width: 100%;
}
.dp__cell_inner {
  border: 0px;
}
.dp__range_start {
  border-end-start-radius: 100px;
  border-start-start-radius: 100px;
}
.dp__range_end {
  border-end-end-radius: 100px;
  border-start-end-radius: 100px;
}
.dp__calendar_header {
  font-weight: normal;
  height: 32px;
}
.dp__main {
  justify-content: center;
  position: relative;
}
.dp__action_row {
  max-width: 10px;
}
.dp__cell_offset.dp--future {
  color: var(--dp-future-offset-color);
}
.dp__range_start.dp__cell_offset.dp--future,
.dp__range_between.dp__cell_offset.dp--future,
.dp__range_end.dp__cell_offset.dp--future {
  color: white;
}
.dp__cell_disabled {
  text-decoration: line-through;
}
</style>
